import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import Toast from 'react-native-toast-message';
import { fpColors } from '../../../styles/theme';
import { PrimaryCTA } from '../../../components/PrimaryCTA';
import { api } from '../../../api/remembrApi';
import { captureScanLocation, type ScanLocation } from '../../../utils/scanLocation';
import {
  searchAddress,
  reverseGeocode,
  type AddressCandidate,
} from '../../../utils/photonClient';
import type { InventoryItemResponse } from '../../../api/types';
import { useAppSelector } from '../../../store/store';
import { selectAllLocations } from '../../browse/browseSlice';
import { selectUser } from '../../auth/authSlice';
import { selectMemberNameById } from '../../sharing/groupsSlice';

/**
 * LocationEditSheet — Phase 3B Memory-Map editor.
 *
 * Two-input model:
 *   1. NAME — answers "Where is X stored?" — chips below for quick-pick
 *      (Kitchen / Garage / etc). Smart prefix matching: typing "kitc"
 *      ghosts "hen" + highlights the Kitchen chip.
 *   2. ADDRESS — search with inline GPS button (📍) on the right end.
 *      Tap GPS → capture phone coords → Photon reverse → fill text.
 *      Type → debounced Photon forward search → suggestion dropdown.
 *
 * Save resolution (see plan § Stage 2C Save resolution matrix):
 *   - Name → `current_location_text` + `location` (back-compat)
 *   - Coords (GPS button OR picked suggestion OR forward-geocode on save)
 *     → `current_location_lat/lng`
 *   - Strict-mode UX: if user typed Address but no coords resolved AND
 *     no Photon match → save blocked with inline error
 *
 * Phase 3D (after BE#143) will pipe structured fields (street/city/
 * state/postcode/country) into the PATCH payload. Until then, FE
 * captures them in component state but doesn't send them.
 */

/** Hardcoded starter chips — shown only when the server returns 0
 *  top locations (brand-new user with no scan history) AND when the
 *  endpoint isn't deployed yet (graceful fallback). Phase 3H wires
 *  in personalized chips from GET /users/locations/top (BE#145). */
const STARTER_CHIPS = [
  'Kitchen',
  'Living Room',
  'Garage',
  'Bedroom',
  'Bathroom',
  'Office',
  'Storage',
  'Basement',
];

interface LocationEditSheetProps {
  visible: boolean;
  item: InventoryItemResponse;
  onClose: () => void;
  onSaved: (updated: InventoryItemResponse) => void;
}

interface CoordsState {
  lat: number;
  lng: number;
  /** 'gps_button' | 'address_picker' | 'manual_edit' — kept in state so
   *  Phase 3F can pass it as the X-Location-Source header on PATCH. */
  source: 'gps_button' | 'address_picker' | 'manual_edit';
}

export const LocationEditSheet: React.FC<LocationEditSheetProps> = ({
  visible,
  item,
  onClose,
  onSaved,
}) => {
  // ── Name input ──────────────────────────────────────────────────
  const initialName =
    ((item as any).current_location_text as string | null | undefined)
    ?? item.location
    ?? '';
  const [name, setName] = useState<string>(initialName);

  // ── Address input ───────────────────────────────────────────────
  const initialAddress =
    ((item as any).current_location_address as string | null | undefined)
    ?? '';
  const [address, setAddress] = useState<string>(initialAddress);
  // Coords stashed silently (not displayed in the input). Populated by
  // GPS button, by picking a Photon suggestion, or by the existing
  // current_location_lat/lng on initial open.
  const [coords, setCoords] = useState<CoordsState | null>(() => {
    const lat = (item as any).current_location_lat;
    const lng = (item as any).current_location_lng;
    if (typeof lat === 'number' && typeof lng === 'number') {
      return { lat, lng, source: 'manual_edit' };
    }
    return null;
  });
  // Structured fields from Photon — captured for Phase 3D once BE#143 ships
  const [structured, setStructured] = useState<Pick<
    AddressCandidate,
    'street' | 'city' | 'state' | 'postcode' | 'country'
  > | null>(null);

  // ── Dropdown / GPS button state ─────────────────────────────────
  const [suggestions, setSuggestions] = useState<AddressCandidate[]>([]);
  const [searching, setSearching] = useState(false);
  const [capturingGps, setCapturingGps] = useState(false);
  const searchAbortRef = useRef<AbortController | null>(null);
  const reverseAbortRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Save state ──────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── "Edit details" disclosure ───────────────────────────────────
  // Hides the 5 structured fields by default; user can expand to fix
  // unit numbers, typos, etc. before save. Edits override Photon's
  // values (backend FE-precedence preserves them).
  const [detailsOpen, setDetailsOpen] = useState(false);

  // ── Phase 3H — Personalized chips ─────────────────────────────────
  // Server-derived top labels (BE#145). Cached for the lifetime of
  // the sheet; falls back to STARTER_CHIPS on 404 (legacy backend)
  // or empty array (new user with no history).
  const [topLabels, setTopLabels] = useState<string[] | null>(null);

  // ── Reset everything on sheet open ──────────────────────────────
  useEffect(() => {
    if (!visible) return;
    setName(initialName);
    setAddress(initialAddress);
    const lat = (item as any).current_location_lat;
    const lng = (item as any).current_location_lng;
    setCoords(
      typeof lat === 'number' && typeof lng === 'number'
        ? { lat, lng, source: 'manual_edit' }
        : null,
    );
    // Re-hydrate structured fields from the item — so reopening an
    // item with previously-saved address details shows them in the
    // "Edit details" disclosure.
    const itStreet = (item as any).current_location_street;
    const itCity = (item as any).current_location_city;
    const itState = (item as any).current_location_state;
    const itPostcode = (item as any).current_location_postcode;
    const itCountry = (item as any).current_location_country;
    if (itStreet || itCity || itState || itPostcode || itCountry) {
      setStructured({
        street: itStreet ?? null,
        city: itCity ?? null,
        state: itState ?? null,
        postcode: itPostcode ?? null,
        country: itCountry ?? null,
      });
    } else {
      setStructured(null);
    }
    setDetailsOpen(false);
    setSuggestions([]);
    setError(null);
    setSearching(false);
    setCapturingGps(false);
  }, [visible, initialName, initialAddress, item]);

  /** Update a single structured field. Marks coords source as
   *  manual_edit so the strict-mode forward-geocode-on-save doesn't
   *  override the user's edits with Photon's canonical version. */
  const updateStructuredField = (
    field: 'street' | 'city' | 'state' | 'postcode' | 'country',
    value: string,
  ) => {
    setStructured((prev) => ({
      street: prev?.street ?? null,
      city: prev?.city ?? null,
      state: prev?.state ?? null,
      postcode: prev?.postcode ?? null,
      country: prev?.country ?? null,
      [field]: value || null,
    }));
  };

  // Cleanup any in-flight aborts on unmount
  useEffect(() => {
    return () => {
      searchAbortRef.current?.abort();
      reverseAbortRef.current?.abort();
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  // Phase 3H — fetch the user's top labels on first open. Memoized
  // per component mount; closing + reopening the sheet doesn't re-
  // fetch unless the component itself remounts.
  useEffect(() => {
    if (!visible || topLabels !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getTopLocations(8);
        if (cancelled) return;
        const labels = (res.locations ?? []).map((l) => l.label);
        setTopLabels(labels);
      } catch (err: any) {
        if (cancelled) return;
        // 404 = endpoint not deployed yet; any other error is non-
        // critical. Either way fall back to STARTER_CHIPS by setting
        // an empty array (the chip resolver below handles that case).
        setTopLabels([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, topLabels]);

  /** The chip set used by both the always-visible "Quick pick" row
   *  and the prefix-match logic. Prefers server's top labels when
   *  available, falls back to hardcoded starter set. Server labels
   *  preserve the user's saved casing ("Kitchen" not "kitchen"). */
  // Search-UX §3.12 — augment with the user's actual existing
  // locations from selectAllLocations.  Server's topLabels may not
  // cover long-tail locations the user has typed; merging in the
  // full set kills the fragmentation that today makes Browse
  // Location bucket noisy ("Garage" vs "garage" vs "Garage shelf
  // 2" vs "shelf 2 garage").  Case-insensitive dedupe; first-seen
  // casing wins (matches Notion picker semantics).
  const allLocations = useAppSelector(selectAllLocations);
  const chipSet = useMemo<string[]>(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    const push = (loc: string) => {
      const k = loc.trim().toLowerCase();
      if (!k || seen.has(k)) return;
      seen.add(k);
      out.push(loc.trim());
    };
    // topLabels first (BE-derived ordering by user signal)
    for (const l of topLabels ?? []) push(l);
    // then existing-inventory long tail
    for (const l of allLocations) push(l.location);
    // then hardcoded starter fallback
    for (const l of STARTER_CHIPS) push(l);
    return out;
  }, [topLabels, allLocations]);

  const scanAddress = (item as any).scan_address as string | null | undefined;

  // ── Chip prefix match (smart matching) ──────────────────────────
  // Uses `chipSet` (server-derived top labels + hardcoded fallback)
  // so users with non-default labels ("Truck cab", "Site") get them
  // ranked first.
  const matchingChips = useMemo(() => {
    const q = name.trim().toLowerCase();
    if (!q) return chipSet;
    const matches = chipSet.filter((c) =>
      c.toLowerCase().startsWith(q),
    );
    return matches.length > 0 ? matches : [];
  }, [name, chipSet]);
  const ghostCompletion = useMemo(() => {
    const q = name.trim();
    if (!q || matchingChips.length === 0) return '';
    const first = matchingChips[0];
    // Match casing of typed prefix
    if (first.toLowerCase().startsWith(q.toLowerCase())) {
      return first.slice(q.length);
    }
    return '';
  }, [name, matchingChips]);
  const selectedChip = useMemo(() => {
    const trimmed = name.trim().toLowerCase();
    return chipSet.find((c) => c.toLowerCase() === trimmed) ?? null;
  }, [name, chipSet]);

  // ── Handlers ────────────────────────────────────────────────────
  const handleChipTap = (chip: string) => {
    setName(chip);
  };

  const handleNameKeyDown = (key: string) => {
    if ((key === 'Tab' || key === 'Enter') && ghostCompletion) {
      setName((prev) => prev + ghostCompletion);
    }
  };

  /** Address-input change handler — debounces Photon search. */
  const handleAddressChange = (value: string) => {
    setAddress(value);
    // When the user starts typing in Address, invalidate any previously
    // picked coords / structured fields (the input is now stale).
    if (coords && coords.source === 'address_picker') {
      setCoords(null);
      setStructured(null);
    }
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (!value.trim() || value.trim().length < 3) {
      setSuggestions([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceTimerRef.current = setTimeout(async () => {
      searchAbortRef.current?.abort();
      searchAbortRef.current = new AbortController();
      const results = await searchAddress(
        value,
        searchAbortRef.current.signal,
        5,
      );
      setSuggestions(results);
      setSearching(false);
    }, 300);
  };

  /** Tap a Photon suggestion — fills input + stashes coords + structured. */
  const handlePickSuggestion = (s: AddressCandidate) => {
    setAddress(s.display_name);
    setCoords({ lat: s.lat, lng: s.lng, source: 'address_picker' });
    setStructured({
      street: s.street,
      city: s.city,
      state: s.state,
      postcode: s.postcode,
      country: s.country,
    });
    setSuggestions([]);
  };

  /** Tap the right-end button:
   *    - Empty state: captures phone GPS + reverse-geocodes.
   *    - Filled state: clears the address + coords. */
  const handleAddressActionPress = async () => {
    if (address.trim()) {
      // Clear
      setAddress('');
      setCoords((c) => (c?.source === 'gps_button' || c?.source === 'address_picker' ? null : c));
      setStructured(null);
      setSuggestions([]);
      return;
    }
    // Capture GPS
    setCapturingGps(true);
    setError(null);
    try {
      const loc = await captureScanLocation();
      if (!loc) {
        setError(
          "Couldn't get your location. Check that location services are on, then try again.",
        );
        return;
      }
      // Reverse-geocode
      reverseAbortRef.current?.abort();
      reverseAbortRef.current = new AbortController();
      const candidate = await reverseGeocode(
        loc.lat,
        loc.lng,
        reverseAbortRef.current.signal,
      );
      setCoords({ lat: loc.lat, lng: loc.lng, source: 'gps_button' });
      if (candidate) {
        setAddress(candidate.display_name);
        setStructured({
          street: candidate.street,
          city: candidate.city,
          state: candidate.state,
          postcode: candidate.postcode,
          country: candidate.country,
        });
      } else {
        // Couldn't resolve to a human-readable name — show the raw coords
        // as a fallback so the user knows we have a pin.
        setAddress(`${loc.lat.toFixed(4)}, ${loc.lng.toFixed(4)}`);
        setStructured(null);
      }
    } finally {
      setCapturingGps(false);
    }
  };

  // ── Save ────────────────────────────────────────────────────────
  const isAddressTyped = address.trim().length > 0;
  const hasNameOrAddress = name.trim().length > 0 || isAddressTyped;
  const isSaveDisabled = !hasNameOrAddress || saving || capturingGps;

  const handleSave = async () => {
    if (!item.id || !hasNameOrAddress) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const trimmedName = name.trim();
      let finalCoords = coords;

      // Strict-mode forward-geocode-on-save:
      // If user typed an Address that hasn't been picked and we have no
      // coords yet, fire one Photon forward call to attempt resolution.
      let finalStructured = structured;
      if (isAddressTyped && !finalCoords) {
        searchAbortRef.current?.abort();
        searchAbortRef.current = new AbortController();
        const results = await searchAddress(
          address,
          searchAbortRef.current.signal,
          1,
        );
        if (results.length > 0) {
          const top = results[0];
          finalCoords = {
            lat: top.lat,
            lng: top.lng,
            source: 'address_picker',
          };
          finalStructured = {
            street: top.street,
            city: top.city,
            state: top.state,
            postcode: top.postcode,
            country: top.country,
          };
          setStructured(finalStructured);
        }
      }

      const patch: Parameters<typeof api.updateInventoryItem>[1] = {
        location: trimmedName || undefined,
        current_location_text: trimmedName || null,
      };
      if (finalCoords) {
        patch.current_location_lat = finalCoords.lat;
        patch.current_location_lng = finalCoords.lng;
      }
      // Stage 2C / Phase 3D — backend#143 now accepts the 5 structured
      // fields. Send whatever Photon gave us; server's FE-precedence
      // rule means our values won't be overwritten by the async
      // reverse-geocode worker (it only fills NULL fields).
      if (finalStructured) {
        if (finalStructured.street) patch.current_location_street = finalStructured.street;
        if (finalStructured.city) patch.current_location_city = finalStructured.city;
        if (finalStructured.state) patch.current_location_state = finalStructured.state;
        if (finalStructured.postcode) patch.current_location_postcode = finalStructured.postcode;
        if (finalStructured.country) patch.current_location_country = finalStructured.country;
      }

      // Phase 3F — tag the location-history event with the origin so
      // the timeline shows the correct icon (📍 GPS button vs 🌐
      // address picker vs ✏️ manual edit). Defaults to MANUAL_EDIT
      // when no coords were captured this round.
      const locationSource: 'gps_button' | 'address_picker' | 'manual_edit' =
        finalCoords?.source === 'gps_button'
          ? 'gps_button'
          : finalCoords?.source === 'address_picker'
            ? 'address_picker'
            : 'manual_edit';
      const updated = await api.updateInventoryItem(item.id, patch, {
        locationSource,
      });
      const merged: InventoryItemResponse = {
        ...item,
        ...updated,
        location: updated.location ?? (trimmedName || undefined),
        current_location_text:
          (updated as any).current_location_text ?? (trimmedName || null),
        current_location_lat:
          (updated as any).current_location_lat
          ?? (finalCoords ? finalCoords.lat : null),
        current_location_lng:
          (updated as any).current_location_lng
          ?? (finalCoords ? finalCoords.lng : null),
        // Backend will reverse-geocode lat/lng → address asynchronously,
        // so the new address may not be in the response yet. Show the
        // typed address as an optimistic placeholder until next fetch.
        current_location_address:
          (updated as any).current_location_address
          ?? (address.trim() || null),
        // Stage 2C — also optimistically merge the structured fields we
        // just sent so the bento + map popup show them without a refetch.
        current_location_street:
          (updated as any).current_location_street
          ?? finalStructured?.street
          ?? (item as any).current_location_street
          ?? null,
        current_location_city:
          (updated as any).current_location_city
          ?? finalStructured?.city
          ?? (item as any).current_location_city
          ?? null,
        current_location_state:
          (updated as any).current_location_state
          ?? finalStructured?.state
          ?? (item as any).current_location_state
          ?? null,
        current_location_postcode:
          (updated as any).current_location_postcode
          ?? finalStructured?.postcode
          ?? (item as any).current_location_postcode
          ?? null,
        current_location_country:
          (updated as any).current_location_country
          ?? finalStructured?.country
          ?? (item as any).current_location_country
          ?? null,
      } as InventoryItemResponse;

      Toast.show({
        type: 'success',
        text1: 'Location saved',
        text2: finalCoords
          ? 'GPS pinned · address resolving…'
          : undefined,
      });
      onSaved(merged);
      onClose();
    } catch (err: any) {
      const detailRaw = err?.response?.data?.detail;
      const detail = Array.isArray(detailRaw)
        ? detailRaw.map((d: any) => d?.msg || JSON.stringify(d)).join(', ')
        : typeof detailRaw === 'string'
          ? detailRaw
          : null;
      setError(detail || err?.message || 'Could not save location.');
    } finally {
      setSaving(false);
    }
  };

  // FE-230 / SD-3 — Owner-while-held read-only popover.
  // When the item is currently with another user (custody is out),
  // the OWNER no longer has location-edit authority — that belongs
  // to the custodian per I-13 / SG-4. Pre-empt the inevitable 403
  // `location_owned_by_custodian` from the server with a read-only
  // popover that explains the state.
  const currentUser = useAppSelector(selectUser);
  const custodianId = ((item as any).current_custodian_user_id ?? null) as
    | number
    | null;
  const ownerId = ((item as any).user_id ?? null) as number | null;
  const viewerIsOwner =
    currentUser?.id != null && ownerId != null && ownerId === currentUser.id;
  const itemIsOut =
    custodianId != null &&
    ownerId != null &&
    custodianId !== ownerId &&
    custodianId !== currentUser?.id;
  const ownerLocked = viewerIsOwner && itemIsOut;
  const custodianName = useAppSelector(selectMemberNameById(custodianId));

  // ── Render ──────────────────────────────────────────────────────
  // Read-only popover when owner can't edit. Smaller modal, plain text,
  // one OK button. Keeps the entry-point gesture consistent (still tap
  // the location bento → modal opens) so users learn the constraint
  // without bouncing into a 403 toast.
  if (ownerLocked) {
    return (
      <Modal
        visible={visible}
        animationType="fade"
        transparent
        onRequestClose={onClose}
      >
        <View style={styles.overlay}>
          <View
            style={[styles.sheet, { paddingBottom: 24 }]}
            accessible
            accessibilityLabel="Location locked while item is out"
            testID="location-locked-popover"
          >
            <View style={styles.header}>
              <Text style={styles.title}>Location locked</Text>
              <Pressable
                onPress={onClose}
                hitSlop={8}
                accessibilityLabel="Close"
                style={styles.closeBtn}
              >
                <MaterialIcons name="close" size={22} color={fpColors.onSurface} />
              </Pressable>
            </View>
            <View style={{ paddingHorizontal: 16, paddingTop: 4, gap: 12 }}>
              <Text style={{ fontSize: 14, color: fpColors.onSurface, lineHeight: 20 }}>
                {custodianName ?? 'The current holder'} updates the location
                while they have this item. You'll regain edit access when
                they hand it back.
              </Text>
              <Text
                style={{
                  fontSize: 12,
                  color: fpColors.onSurfaceVariant,
                  lineHeight: 16,
                }}
              >
                You can still send a reminder or recall from Item details.
              </Text>
              <PrimaryCTA label="OK" onPress={onClose} />
            </View>
          </View>
        </View>
      </Modal>
    );
  }

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View
          style={styles.sheet}
          accessible
          accessibilityViewIsModal
          accessibilityLabel="Update location"
          // @ts-ignore — RN Web only
          role="dialog"
          aria-modal={true}
          testID="location-edit-sheet"
        >
          <View style={styles.header}>
            <Text style={styles.title}>Update location</Text>
            <Pressable
              onPress={onClose}
              hitSlop={8}
              accessibilityLabel="Close"
              accessibilityRole="button"
              style={styles.closeBtn}
              testID="location-edit-close"
            >
              <MaterialIcons name="close" size={22} color={fpColors.onSurface} />
            </Pressable>
          </View>

          <ScrollView
            style={styles.scroll}
            contentContainerStyle={styles.scrollContent}
            keyboardShouldPersistTaps="handled"
          >
            {/* ─── Section 1: Name ─────────────────────────────────── */}
            <Text style={styles.fieldLabel}>
              Where is &ldquo;{item.item_name ?? 'this item'}&rdquo; stored?
            </Text>
            <View style={styles.nameInputWrap}>
              <TextInput
                value={name}
                onChangeText={setName}
                onKeyPress={(e) => {
                  // @ts-ignore — web key event
                  handleNameKeyDown(e.nativeEvent.key);
                }}
                placeholder="e.g. kitchen, garage shelf 2"
                placeholderTextColor={fpColors.outline}
                maxLength={120}
                style={styles.nameInput}
                accessibilityLabel="Name"
                testID="location-edit-name"
              />
              {ghostCompletion ? (
                <View style={styles.ghostText} pointerEvents="none">
                  <Text style={styles.ghostTextRow}>
                    <Text style={{ color: 'transparent' }}>{name}</Text>
                    <Text style={styles.ghostTextSuffix}>{ghostCompletion}</Text>
                  </Text>
                </View>
              ) : null}
            </View>
            {ghostCompletion ? (
              <Text style={styles.hintText}>
                Tab or Enter to accept &ldquo;{name}{ghostCompletion}&rdquo;
              </Text>
            ) : name.trim() && matchingChips.length === 0 ? (
              <Text style={styles.hintText}>Will save as a custom label</Text>
            ) : null}

            {matchingChips.length > 0 ? (
              <>
                <Text style={styles.chipLabel}>
                  {name.trim() ? 'Matching' : 'Quick pick'}
                </Text>
                <View style={styles.chipRow}>
                  {matchingChips.map((chip) => {
                    const isSelected = selectedChip === chip;
                    return (
                      <Pressable
                        key={chip}
                        onPress={() => handleChipTap(chip)}
                        accessibilityRole="button"
                        accessibilityLabel={`Pick label ${chip}`}
                        testID={`location-edit-chip-${chip.toLowerCase().replace(/\s+/g, '-')}`}
                        style={({ pressed }) => [
                          styles.chip,
                          isSelected && styles.chipActive,
                          pressed && { opacity: 0.85 },
                        ]}
                      >
                        {isSelected ? (
                          <MaterialIcons
                            name="check"
                            size={14}
                            color={fpColors.onPrimary}
                            style={{ marginRight: 4 }}
                          />
                        ) : null}
                        <Text
                          style={[
                            styles.chipText,
                            isSelected && styles.chipTextActive,
                          ]}
                        >
                          {chip}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              </>
            ) : null}

            <View style={styles.sectionDivider} />

            {/* ─── Section 2: Address ──────────────────────────────── */}
            <Text style={styles.fieldLabel}>Address</Text>
            <View style={styles.addressInputWrap}>
              <TextInput
                value={address}
                onChangeText={handleAddressChange}
                placeholder="Search address or tap pin →"
                placeholderTextColor={fpColors.outline}
                style={styles.addressInput}
                accessibilityLabel="Address"
                testID="location-edit-address"
              />
              <Pressable
                onPress={handleAddressActionPress}
                disabled={capturingGps}
                hitSlop={8}
                accessibilityRole="button"
                accessibilityLabel={
                  address.trim() ? 'Clear address' : 'Use my current GPS'
                }
                testID="location-edit-gps"
                style={({ pressed }) => [
                  styles.addressBtn,
                  pressed && { opacity: 0.85 },
                ]}
              >
                {capturingGps ? (
                  <ActivityIndicator size="small" color={fpColors.primary} />
                ) : address.trim() ? (
                  <MaterialIcons
                    name="close"
                    size={20}
                    color={fpColors.onSurfaceVariant}
                  />
                ) : (
                  <MaterialIcons
                    name="my-location"
                    size={20}
                    color={fpColors.primary}
                  />
                )}
              </Pressable>
            </View>

            {coords ? (
              <Text style={styles.captionGps}>
                {coords.source === 'gps_button' ? 'GPS captured' : 'GPS attached from address'}
                : {coords.lat.toFixed(4)}, {coords.lng.toFixed(4)}
              </Text>
            ) : null}

            {searching && address.trim().length >= 3 ? (
              <View style={styles.suggestionLoading}>
                <ActivityIndicator size="small" color={fpColors.primary} />
                <Text style={styles.suggestionLoadingText}>Searching…</Text>
              </View>
            ) : null}

            {suggestions.length > 0 ? (
              <View style={styles.suggestionList} testID="location-edit-suggestions">
                {suggestions.map((s, idx) => (
                  <Pressable
                    key={`${s.lat}-${s.lng}-${idx}`}
                    onPress={() => handlePickSuggestion(s)}
                    accessibilityRole="button"
                    accessibilityLabel={`Pick address ${s.display_name}`}
                    testID={`location-edit-suggestion-${idx}`}
                    style={({ pressed }) => [
                      styles.suggestion,
                      pressed && { opacity: 0.85 },
                      idx < suggestions.length - 1 && styles.suggestionDivider,
                    ]}
                  >
                    <MaterialIcons
                      name="location-on"
                      size={16}
                      color={fpColors.primary}
                    />
                    <View style={{ flex: 1 }}>
                      <Text style={styles.suggestionTitle} numberOfLines={1}>
                        {s.name || s.street || s.display_name}
                      </Text>
                      <Text style={styles.suggestionMeta} numberOfLines={1}>
                        {[s.city, s.state, s.country].filter(Boolean).join(', ')}
                      </Text>
                    </View>
                  </Pressable>
                ))}
              </View>
            ) : null}

            {/* "Edit details" disclosure — exposes the 5 structured
                address fields for manual edits (apartment numbers,
                typos). Visible only when there's something to show. */}
            {(structured && (structured.street || structured.city || structured.state || structured.postcode || structured.country)) || isAddressTyped ? (
              <Pressable
                onPress={() => setDetailsOpen((v) => !v)}
                accessibilityRole="button"
                accessibilityLabel={detailsOpen ? 'Hide address details' : 'Edit address details'}
                testID="location-edit-details-toggle"
                style={styles.detailsToggle}
              >
                <MaterialIcons
                  name={detailsOpen ? 'expand-less' : 'expand-more'}
                  size={18}
                  color={fpColors.primary}
                />
                <Text style={styles.detailsToggleText}>
                  {detailsOpen ? 'Hide address details' : 'Edit address details'}
                </Text>
              </Pressable>
            ) : null}

            {detailsOpen ? (
              <View style={styles.detailsPanel} testID="location-edit-details-panel">
                <StructuredField
                  label="Street"
                  value={structured?.street ?? ''}
                  placeholder="1234 Saddleridge Way SE"
                  onChange={(v) => updateStructuredField('street', v)}
                  testID="location-edit-street"
                />
                <StructuredField
                  label="City"
                  value={structured?.city ?? ''}
                  placeholder="Calgary"
                  onChange={(v) => updateStructuredField('city', v)}
                  testID="location-edit-city"
                />
                <View style={styles.detailsRow}>
                  <View style={{ flex: 1 }}>
                    <StructuredField
                      label="State / Province"
                      value={structured?.state ?? ''}
                      placeholder="Alberta"
                      onChange={(v) => updateStructuredField('state', v)}
                      testID="location-edit-state"
                    />
                  </View>
                  <View style={{ width: 12 }} />
                  <View style={{ flex: 1 }}>
                    <StructuredField
                      label="Postcode"
                      value={structured?.postcode ?? ''}
                      placeholder="T3J 5W8"
                      onChange={(v) => updateStructuredField('postcode', v)}
                      testID="location-edit-postcode"
                    />
                  </View>
                </View>
                <StructuredField
                  label="Country (ISO-3166)"
                  value={structured?.country ?? ''}
                  placeholder="CA"
                  onChange={(v) =>
                    updateStructuredField('country', v.toUpperCase().slice(0, 2))
                  }
                  maxLength={2}
                  testID="location-edit-country"
                />
              </View>
            ) : null}

            {scanAddress ? (
              <View style={styles.scanChip}>
                <MaterialIcons
                  name="location-on"
                  size={14}
                  color={fpColors.tertiary}
                />
                <Text style={styles.scanChipText} numberOfLines={2}>
                  Scanned at: {scanAddress}
                </Text>
              </View>
            ) : null}

            {error ? (
              <View style={styles.errorBanner} testID="location-edit-error">
                <MaterialIcons
                  name="error-outline"
                  size={16}
                  color={fpColors.error}
                />
                <Text style={styles.errorText}>{error}</Text>
              </View>
            ) : null}
          </ScrollView>

          <View style={styles.footer}>
            <Pressable
              onPress={onClose}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel="Cancel"
              style={styles.cancelBtn}
              testID="location-edit-cancel"
            >
              <Text style={styles.cancelText}>Cancel</Text>
            </Pressable>
            <View style={styles.saveBtnWrap} testID="location-edit-save">
              <PrimaryCTA
                label={saving ? 'Saving…' : 'Save'}
                onPress={handleSave}
                disabled={isSaveDisabled}
              />
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

interface StructuredFieldProps {
  label: string;
  value: string;
  placeholder: string;
  onChange: (v: string) => void;
  maxLength?: number;
  testID?: string;
}

/** Small labeled text input used by the "Edit details" disclosure. */
const StructuredField: React.FC<StructuredFieldProps> = ({
  label,
  value,
  placeholder,
  onChange,
  maxLength,
  testID,
}) => (
  <View style={fieldStyles.wrap}>
    <Text style={fieldStyles.label}>{label}</Text>
    <TextInput
      value={value}
      onChangeText={onChange}
      placeholder={placeholder}
      placeholderTextColor={fpColors.outline}
      maxLength={maxLength}
      accessibilityLabel={label}
      testID={testID}
      style={fieldStyles.input}
    />
  </View>
);

const fieldStyles = StyleSheet.create({
  wrap: {
    marginBottom: 8,
  },
  label: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 4,
  },
  input: {
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    color: fpColors.onSurface,
    backgroundColor: fpColors.surfaceContainerLow,
  },
});

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(28, 23, 33, 0.55)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: fpColors.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingBottom: Platform.OS === 'ios' ? 24 : 16,
    maxHeight: '92%',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 18,
    paddingBottom: 14,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
  },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 18,
    color: fpColors.onSurface,
  },
  closeBtn: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 16,
  },
  scroll: { flexGrow: 0 },
  scrollContent: { padding: 20, paddingBottom: 8, gap: 8 },
  fieldLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onSurface,
    marginBottom: 4,
  },
  // ── Name input + ghost text ──────────────────────────────────────
  nameInputWrap: {
    position: 'relative' as any,
  },
  nameInput: {
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontFamily: 'Inter_500Medium',
    fontSize: 15,
    color: fpColors.onSurface,
    backgroundColor: fpColors.surfaceContainerLow,
  },
  ghostText: {
    position: 'absolute' as any,
    top: 12,
    left: 14,
    right: 14,
  },
  ghostTextRow: {
    fontFamily: 'Inter_500Medium',
    fontSize: 15,
  },
  ghostTextSuffix: {
    color: fpColors.outline,
  },
  hintText: {
    fontFamily: 'Inter_400Regular',
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
    marginTop: 2,
  },
  chipLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: 10,
    marginBottom: 4,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surfaceContainerLow,
  },
  chipActive: {
    backgroundColor: fpColors.primary,
    borderColor: fpColors.primary,
  },
  chipText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onSurface,
  },
  chipTextActive: {
    color: fpColors.onPrimary,
    fontFamily: 'Inter_700Bold',
  },
  // ── Section divider ──────────────────────────────────────────────
  sectionDivider: {
    height: 1,
    backgroundColor: fpColors.outlineVariant,
    marginVertical: 14,
  },
  // ── Address input + inline button ────────────────────────────────
  addressInputWrap: {
    flexDirection: 'row',
    alignItems: 'stretch',
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    borderRadius: 12,
    backgroundColor: fpColors.surfaceContainerLow,
    overflow: 'hidden',
  },
  addressInput: {
    flex: 1,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontFamily: 'Inter_500Medium',
    fontSize: 15,
    color: fpColors.onSurface,
  },
  addressBtn: {
    width: 48,
    alignItems: 'center',
    justifyContent: 'center',
    borderLeftWidth: 1,
    borderLeftColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surface,
  },
  captionGps: {
    fontFamily: 'Inter_400Regular',
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
    marginTop: 4,
  },
  suggestionLoading: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 6,
  },
  suggestionLoadingText: {
    fontFamily: 'Inter_400Regular',
    fontSize: 11,
    color: fpColors.outline,
  },
  suggestionList: {
    marginTop: 6,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    borderRadius: 12,
    backgroundColor: fpColors.surface,
    overflow: 'hidden',
  },
  suggestion: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  suggestionDivider: {
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
  },
  suggestionTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onSurface,
  },
  suggestionMeta: {
    fontFamily: 'Inter_400Regular',
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
  },
  detailsToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 10,
    paddingVertical: 6,
  },
  detailsToggleText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.primary,
  },
  detailsPanel: {
    marginTop: 4,
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surface,
  },
  detailsRow: {
    flexDirection: 'row',
  },
  scanChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: `${fpColors.tertiaryContainer}14`,
    borderRadius: 10,
    marginTop: 12,
  },
  scanChipText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: `${fpColors.errorContainer}40`,
    borderRadius: 10,
    marginTop: 12,
  },
  errorText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.error,
  },
  // ── Footer ───────────────────────────────────────────────────────
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderTopWidth: 1,
    borderTopColor: fpColors.outlineVariant,
  },
  cancelBtn: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
  },
  cancelText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurfaceVariant,
  },
  saveBtnWrap: {
    flex: 1,
  },
});

export default LocationEditSheet;
